﻿/**
 * Covermatic Studio application bootstrap.
 */
(() => {
  const runtime = window.CovermaticRuntime || window.AlphaRuntime || {};
  window.CovermaticRuntime = runtime;
  window.AlphaRuntime = runtime;
  const {
    Config,
    State,
    Utils,
    ApiClient,
    Events,
    FileUploader,
    FlowController,
    FormController,
    CreativeLabController,
    TaskManager,
    GalleryController,
    ModalController,
  } = runtime;

  const App = {
    async init() {
      this.gallery = runtime.registerService('gallery', null);
      this.flow = runtime.registerService('flow', new FlowController());
      this.form = runtime.registerService('form', new FormController());
      if (typeof CreativeLabController === 'function' && Utils.$('creativeLabPanel')) {
        this.creativeLab = runtime.registerService('creativeLab', new CreativeLabController());
      } else {
        this.creativeLab = runtime.registerService('creativeLab', null);
      }
      this.modals = runtime.registerService('modals', new ModalController());
      if (Utils.isLoggedIn()) document.body.classList.add('covermatic-chat-launcher-hidden');

      this.startBackgroundWarmup();

      this.genUploader = runtime.registerService('genUploader', new FileUploader('uploadZone', '#photo', 'uploadPreview'));
      this.editUploader = runtime.registerService('editUploader', new FileUploader('editUploadZone', 'editPhoto', 'editUploadPreview'));
      runtime.registerService('app', this);

      this.bindEvents();
      this.attachHelpChatObserver();
      TaskManager.init();
      this.initDeferredServices();
      this.patchGalleryHistoryFetch();
      if (Utils.isLoggedIn()) await this.refreshCredits(0);
      this.syncUI();
      try {
        this.initSignatureCardSlideshow();
      } catch (error) {
        console.warn('[Covermatic] Signature slideshow init skipped', error);
      }
      this.handleUrlParams().catch((error) => {
        console.warn('[Covermatic] URL parameter handling skipped', error);
      });
    },

    startBackgroundWarmup() {
      const warmSignatureStyles = () => {
        this.modals?.preloadSignatureStyles?.().catch((error) => {
          console.warn('[Covermatic] Signature style preload skipped', error);
        });
      };

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(warmSignatureStyles, { timeout: 2000 });
      } else {
        window.setTimeout(warmSignatureStyles, 1200);
      }
    },

    patchGalleryHistoryFetch() {
      if (!GalleryController?.prototype || GalleryController.prototype.__covermaticHistoryPatch) return;
      GalleryController.prototype.__covermaticHistoryPatch = true;
      GalleryController.prototype.fetchHistoricalPage = async function fetchHistoricalPagePatched() {
        const apiBase = String(Config.apiBase || '/').replace(/\/+$/, '');
        const historicalQuery = new URLSearchParams({
          customerId: Config.customerId || '',
          limit: String(Config.perFetch),
          offset: String(State.page * Config.perFetch),
        });
        ApiClient.appendToolContext?.(historicalQuery);
        const candidates = [
          `${apiBase}/app_proxy/historical-generations?${historicalQuery}`,
          `${apiBase}/api/historical-generations?${historicalQuery}`,
          `${apiBase}/historical-generations?${historicalQuery}`,
        ];
        const failures = [];
        for (const url of candidates) {
          const response = await Utils.withTimeout(url, { credentials: 'same-origin' }, 8000);
          if (response.ok) return response.json();
          const body = await response.text().catch(() => '');
          failures.push({ url, status: response.status, body: body.slice(0, 180) });
          if (response.status !== 401 && response.status !== 404) {
            throw new Error(`Historical generations request failed: ${JSON.stringify(failures)}`);
          }
        }
        const legacyQuery = new URLSearchParams({
          customerId: Config.customerId || '',
          start: String(State.page * Config.perFetch),
          count: String(Config.perFetch),
        });
        const legacyUrl = `${apiBase}/images?${legacyQuery}`;
        const legacyResponse = await Utils.withTimeout(legacyUrl, { credentials: 'same-origin' }, 8000);
        if (!legacyResponse.ok) {
          const body = await legacyResponse.text().catch(() => '');
          failures.push({ url: legacyUrl, status: legacyResponse.status, body: body.slice(0, 180) });
          throw new Error(`Gallery history fetch failed: ${JSON.stringify(failures)}`);
        }
        return legacyResponse.json();
      };
    },

    initDeferredServices() {
      const bootGallery = () => {
        if (this.gallery) return;
        this.gallery = runtime.registerService('gallery', new GalleryController());
      };

      window.setTimeout(() => {
        try {
          bootGallery();
        } catch (error) {
          console.warn('[Covermatic] Gallery boot skipped', error);
        }
      }, 0);
    },

    bindEvents() {
      const editOpen = Utils.$('editOpenButton');
      if (editOpen) {
        editOpen.addEventListener('click', () => {
          const panel = Utils.$('editPanel');
          const isOpen = !!(panel && !panel.classList.contains('hidden'));
          if (isOpen) {
            this.closeEditPanel();
            return;
          }
          this.openEditPanel();
        });
      }

      const editCloseMobile = Utils.$('editCloseMobileButton');
      if (editCloseMobile) editCloseMobile.addEventListener('click', () => this.closeEditPanel());

      const editReferenceOpen = Utils.$('editReferenceOpen');
      if (editReferenceOpen) editReferenceOpen.addEventListener('click', () => this.openEditReferencePreview());

      const editReferenceClose = Utils.$('editReferenceClose');
      if (editReferenceClose) editReferenceClose.addEventListener('click', () => this.closeEditReferencePreview());

      const editReferenceLightbox = Utils.$('editReferenceLightbox');
      const modalRoot = Utils.$('imageModal');
      if (editReferenceLightbox && modalRoot && editReferenceLightbox.parentElement !== modalRoot) {
        modalRoot.appendChild(editReferenceLightbox);
      }

      const modalImg = Utils.$('modalImage');
      if (modalImg) {
        modalImg.addEventListener('click', () => {
          const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
          const panel = Utils.$('editPanel');
          const panelOpen = !!(panel && !panel.classList.contains('hidden'));
          if (!isDesktop || !panelOpen || !Utils.isLoggedIn()) return;
          this.openEditReferencePreview();
        });
      }

      if (editReferenceLightbox) {
        editReferenceLightbox.addEventListener('click', (event) => {
          if (event.target === editReferenceLightbox) this.closeEditReferencePreview();
        });
      }

      Utils.$('downloadButton')?.addEventListener('click', () => {
        if (!Utils.isLoggedIn()) {
          Utils.toast('Log in required');
          return;
        }

        const a = document.createElement('a');
        a.href = State.currentUrl;
        a.download = 'cover.png';
        a.click();
      });

      Utils.$('shareButton')?.addEventListener('click', () => {
        this.shareArtwork(State.currentImageRecord);
      });

      const promoGraphicsOpen = Utils.$('promoGraphicsOpenButton');
      if (promoGraphicsOpen) {
        promoGraphicsOpen.addEventListener('click', () => {
          this.prepareCurrentArtworkForPromoGraphics();
          this.modals.closeImageModal();
          this.modals.openPromoGraphicsModal();
        });
      }

      const signInLink = Utils.$('buyModalSignInLink');
      if (signInLink) {
        const base = signInLink.dataset.loginBase || '/account/login';
        const returnUrl = window.location.pathname + window.location.search;
        signInLink.href = `${base}?checkout_url=${encodeURIComponent(returnUrl)}`;
      }

      const helpButton = Utils.$('open-help');
      if (helpButton) {
        helpButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.openHelpChat();
        });
      }

      Events.on('credits:refresh-requested', ({ detail }) => {
        this.refreshCredits(detail.fallbackDelta);
      });

      Events.on('modal:buy-open-requested', () => this.openBuyModal());
      Events.on('modal:image-open-requested', ({ detail }) => {
        if (detail?.record || detail?.url) {
          this.modals.openImage(detail.record || detail.url);
          window.setTimeout(() => this.syncShareControls(State.currentImageRecord), 0);
        }
      });

      Events.on('artwork:share-requested', ({ detail }) => {
        this.shareArtwork(detail?.record);
      });

      Events.on('state:credits', ({ detail }) => {
        const creditsEl = Utils.$('creditsValue');
        if (creditsEl) creditsEl.textContent = detail.value;
        this.syncUI();
      });

      Events.on('state:activeUploads', () => {
        this.syncUI();
      });
    },

    prepareCurrentArtworkForPromoGraphics() {
      const record = this.gallery?.resolveRecord?.(State.currentImageRecord || State.currentUrl) || State.currentImageRecord || null;
      const url = String(record?.url || State.currentUrl || '').trim();
      if (!url) {
        State.pendingPromoGraphicAsset = null;
        return null;
      }

      const titleParts = [
        String(record?.artistName || '').trim(),
        String(record?.songName || '').trim(),
      ].filter(Boolean);

      const asset = {
        url,
        title: titleParts.length ? titleParts.join(' - ') : 'Current cover art',
        fileName: String(record?.fileName || record?.uniqueFileName || '').trim() || null,
        assetFamily: record?.assetFamily || 'cover_art',
        assetType: record?.assetType || 'cover_art',
        sizePreset: record?.sizePreset || 'square_1_1',
        source: 'current-cover',
      };

      State.pendingPromoGraphicAsset = asset;
      return asset;
    },

    syncShareControls(record = State.currentImageRecord) {
      const shareButton = Utils.$('shareButton');
      if (!shareButton) return;
      const isShared = record?.sharedWithDsgfx === true;
      const isFreeGeneration = record?.isFreeGeneration === true;
      shareButton.classList.toggle('hidden', isFreeGeneration);
      shareButton.disabled = isFreeGeneration || !Utils.isLoggedIn() || !record?.url || isShared;
      shareButton.textContent = isShared ? 'Shared +2 Credits' : 'Share +2 Credits';
    },

    markArtworkShared(record, payload = {}) {
      if (!record) return;
      record.sharedWithDsgfx = true;
      record.shareCreditsAwarded = Number(payload.rewardCredits || payload.creditsAwarded || 2);
      Utils.$$('[data-covermatic-gallery-card]').forEach((card) => {
        const sameId = record.id != null && card.dataset.recordId === String(record.id);
        const sameFile = record.fileName && card.dataset.fileName === record.fileName;
        if (!sameId && !sameFile) return;
        card.dataset.sharedWithDsgfx = 'true';
        card.dataset.shareCreditsAwarded = String(record.shareCreditsAwarded || 2);
        const share = card.querySelector('[data-card-action="share"]');
        if (share) {
          share.textContent = 'Shared +2';
          share.disabled = true;
        }
      });
      this.syncShareControls(record);
    },

    async shareArtwork(inputRecord) {
      if (!Utils.isLoggedIn()) {
        Utils.toast('Log in required');
        return;
      }
      const record = this.gallery?.resolveRecord?.(inputRecord || State.currentImageRecord) || inputRecord || State.currentImageRecord;
      if (!record?.url || (!record.id && !record.fileName)) {
        Utils.toast('Open a saved artwork before sharing.', 'error');
        return;
      }
      if (record.isFreeGeneration === true) {
        Utils.toast('Free cover previews cannot be shared for credits.', 'warn');
        this.syncShareControls(record);
        return;
      }
      if (record.sharedWithDsgfx) {
        Utils.toast('Already shared for credits.');
        this.syncShareControls(record);
        return;
      }
      const confirmed = window.confirm('Give DSGFX permission to post this artwork for marketing and social use? You will receive 2 credits for this cover.');
      if (!confirmed) return;

      const shareButton = Utils.$('shareButton');
      if (shareButton && State.currentImageRecord?.url === record.url) {
        shareButton.disabled = true;
        shareButton.textContent = 'Sharing...';
      }
      try {
        const payload = await ApiClient.shareArtwork(record);
        if (typeof payload.credits === 'number') State.credits = payload.credits;
        this.markArtworkShared(record, payload);
        Utils.toast(payload.alreadyShared ? 'Already shared for credits.' : 'Shared with DSGFX. +2 credits added.');
      } catch (error) {
        Utils.toast(error?.message || 'Share failed. Please try again.', 'error');
        this.syncShareControls(record);
      }
    },

    initSignatureCardSlideshow() {
      Utils.$$('[data-signature-style-preview]').forEach((slideshow) => {
        const styleId = slideshow.dataset.signatureStylePreview;
        const style = (Config.signatureStyles || []).find((item) => item.id === styleId);
        const previewAssets = (style?.assets || []).filter((asset) => asset && asset.imageUrl).slice(0, 12);
        if (!previewAssets.length) return;

        slideshow.innerHTML = previewAssets.map((asset, index) => `
          <div class="studio-signature-slide${index === 0 ? ' is-active' : ''}" data-signature-slide>
            <img src="${Utils.escapeHtml(asset.imageUrl)}" alt="${Utils.escapeHtml(asset.title || `${style.name} preview ${index + 1}`)}" loading="lazy" decoding="async" />
          </div>
        `).join('');
      });

      Utils.initSignatureSlideshows();
    },

    resetEditPanelPlacement() {
      const panel = Utils.$('editPanel');
      const anchor = Utils.$('editPanelAnchor');
      if (!panel || !anchor) return;
      if (panel.parentElement !== anchor.parentElement || panel.previousElementSibling !== anchor) {
        anchor.insertAdjacentElement('afterend', panel);
      }
    },

    updateEditReferencePreview(url = State.currentUrl) {
      const wrap = Utils.$('editReferenceWrap');
      const thumb = Utils.$('editReferenceThumb');
      const large = Utils.$('editReferenceLarge');
      if (!large) return;

      if (!url) {
        if (wrap) wrap.classList.add('hidden');
        if (thumb) thumb.removeAttribute('src');
        large.removeAttribute('src');
        return;
      }

      if (wrap) wrap.classList.remove('hidden');
      if (thumb && thumb.src !== url) thumb.src = url;
      if (large.src !== url) large.src = url;
    },

    setModalImageExpandability(enabled) {
      const modalImg = Utils.$('modalImage');
      if (!modalImg) return;
      modalImg.classList.toggle('is-expandable', !!enabled);
    },

    openEditReferencePreview() {
      const lightbox = Utils.$('editReferenceLightbox');
      const large = Utils.$('editReferenceLarge');
      if (!lightbox || !large) return;
      const url = State.currentUrl || large.getAttribute('src');
      if (!url) return;
      large.src = url;
      lightbox.classList.remove('hidden');
    },

    closeEditReferencePreview() {
      Utils.$('editReferenceLightbox')?.classList.add('hidden');
    },

    openEditPanel() {
      const panel = Utils.$('editPanel');
      if (!panel) return;

      const overlay = Utils.$('editOverlayMobile');
      const overlaySlot = Utils.$('editOverlayMobileSlot');
      const isMobile = window.matchMedia('(max-width: 1023px)').matches;

      if (isMobile && overlay && overlaySlot) {
        overlaySlot.appendChild(panel);
        overlay.classList.remove('hidden');
        Utils.$('imageModal')?.classList.add('editing-mobile');
        this.setModalImageExpandability(false);
      } else {
        this.resetEditPanelPlacement();
        this.setModalImageExpandability(true);
      }

      this.updateEditReferencePreview();
      panel.classList.remove('hidden');
      Utils.$('editText')?.focus();
    },

    closeEditPanel() {
      const panel = Utils.$('editPanel');
      this.closeEditReferencePreview();
      if (panel) panel.classList.add('hidden');
      Utils.$('editOverlayMobile')?.classList.add('hidden');
      Utils.$('imageModal')?.classList.remove('editing-mobile');
      this.setModalImageExpandability(false);
      this.resetEditPanelPlacement();
    },

    async handleUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const handle = params.get('premade_template');
      let id = params.get('premade_id');
      if (!handle && !id) return;
      if (handle && !/^[a-z0-9-]{1,200}$/.test(handle)) return;
      if (id && !/^\d{9,20}$/.test(id)) return;
      if (!id && handle) {
        const card = document.querySelector(`.tpl-card[data-handle="${handle}"]`);
        id = card?.dataset?.id;
        if (!id) {
          try {
            const response = await fetch('/products/' + encodeURIComponent(handle) + '.json');
            if (!response.ok) return;
            id = String((await response.json()).product?.id || '');
          } catch (_) { return; }
        }
      }
      if (!/^\d{9,20}$/.test(String(id))) return;
      try {
        const response = await fetch('/apps/covermatic/signature-design/' + encodeURIComponent(id), { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const design = await response.json();
        if (String(design.product_id) !== String(id) || (handle && design.handle !== handle)) return;
        const image = new URL(design.template_image || design.image);
        if (image.protocol !== 'https:' || image.username || image.password || !['cdn.shopify.com', 'dontsleepgfx.com', 'www.dontsleepgfx.com'].includes(image.hostname)) return;
        await this.modals.selectTemplate({ id: String(id), handle: design.handle, title: design.title, imageUrl: image.href, kind: 'premade' });
      } catch (_) {
        console.error('[Covermatic] Selected direction is unavailable.');
      }
    },

    openBuyModal() {
      this.modals.openBuyModal();
    },

    openPromoGraphicsModal() {
      this.modals.openPromoGraphicsModal();
    },

    getHelpChatHost() {
      return document.querySelector('inbox-online-store-chat#ShopifyChat')
        || document.querySelector('#shopify-chat inbox-online-store-chat')
        || document.querySelector('inbox-online-store-chat')
        || document.querySelector('shopify-chat, #ShopifyChat, #shopify-chat, div#shopify-chat');
    },

    getHelpChatToggleControl() {
      const host = this.getHelpChatHost();
      if (!host?.shadowRoot) return null;

      return host.shadowRoot.querySelector(
        'button[part="activator"], button[name="closeChat"], button.chat-app--close-button, button[aria-controls="chat-ui"]',
      );
    },

    isHelpChatOpen() {
      const host = this.getHelpChatHost();
      if (host) {
        if (host.getAttribute('is-open') === 'true') return true;
        if (host.hasAttribute('open') && host.getAttribute('open') !== 'false') return true;
        if (host.getAttribute('aria-expanded') === 'true') return true;
        if (host.classList?.contains('is-open')) return true;
        if (host.shadowRoot?.querySelector('button[part="activator"][aria-expanded="true"], button[name="closeChat"], button.chat-app--close-button, button[aria-controls="chat-ui"][aria-expanded="true"]')) return true;
      }

      const iframe = document.querySelector('iframe[src*="inbox.shopify.com"], iframe[src*="chat.shopify.com"]');
      if (iframe && iframe.style.display !== 'none' && iframe.getAttribute('aria-hidden') !== 'true') return true;

      return false;
    },

    setHelpChatLauncherHidden(hidden) {
      if (!Utils.isLoggedIn()) return;
      document.body.classList.toggle('covermatic-chat-launcher-hidden', hidden);
    },

    syncHelpChatLauncher() {
      if (!Utils.isLoggedIn()) return;
      this.setHelpChatLauncherHidden(!this.isHelpChatOpen());
    },

    attachHelpChatObserver() {
      if (!Utils.isLoggedIn() || this.helpChatObserver) return;

      this.helpChatObserver = new MutationObserver(() => {
        this.syncHelpChatLauncher();
      });

      this.helpChatObserver.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['is-open', 'open', 'aria-expanded', 'aria-hidden', 'class', 'style'],
      });

      window.setTimeout(() => this.syncHelpChatLauncher(), 250);
      window.setTimeout(() => this.syncHelpChatLauncher(), 1200);
    },

    openHelpChat() {
      const desiredOpen = !this.isHelpChatOpen();

      const tryToggle = () => {
        if (desiredOpen) this.setHelpChatLauncherHidden(false);

        const directOpeners = [
          () => {
            if (typeof window.ShopifyInbox?.open !== 'function' || !desiredOpen) return false;
            window.ShopifyInbox.open();
            return true;
          },
          () => {
            if (typeof window.ShopifyInbox?.show !== 'function' || !desiredOpen) return false;
            window.ShopifyInbox.show();
            return true;
          },
          () => {
            if (typeof window.ShopifyChat?.open !== 'function' || !desiredOpen) return false;
            window.ShopifyChat.open();
            return true;
          },
          () => {
            if (typeof window.ShopifyChat?.show !== 'function' || !desiredOpen) return false;
            window.ShopifyChat.show();
            return true;
          },
        ];

        for (const open of directOpeners) {
          try {
            if (open()) return true;
          } catch (error) {
            console.warn('[Covermatic] Direct chat opener failed', error);
          }
        }

        const chatHost = this.getHelpChatHost();
        if (chatHost) {
          try {
            if (desiredOpen && typeof chatHost.open === 'function') {
              chatHost.open();
              return true;
            }
            if (desiredOpen && typeof chatHost.show === 'function') {
              chatHost.show();
              return true;
            }
            const shadowButton = this.getHelpChatToggleControl();
            if (shadowButton) {
              shadowButton.click();
              return false;
            }
            if (chatHost.tagName?.toLowerCase() === 'inbox-online-store-chat') {
              chatHost.setAttribute('is-open', desiredOpen ? 'true' : 'false');
              return false;
            }
          } catch (error) {
            console.warn('[Covermatic] Chat host open failed', error);
          }
        }

        const iframe = document.querySelector('iframe[src*="inbox.shopify.com"], iframe[src*="chat.shopify.com"]');
        if (iframe) {
          iframe.scrollIntoView({ behavior: 'smooth', block: 'end' });
          return false;
        }

        return false;
      };

      let attemptsRemaining = 8;
      const attemptToggle = () => {
        tryToggle();

        window.setTimeout(() => {
          const open = this.isHelpChatOpen();
          if (open === desiredOpen) {
            this.syncHelpChatLauncher();
            window.setTimeout(() => this.syncHelpChatLauncher(), 700);
            window.setTimeout(() => this.syncHelpChatLauncher(), 1800);
            return;
          }

          attemptsRemaining -= 1;
          if (attemptsRemaining <= 0) {
            this.syncHelpChatLauncher();
            Utils.toast('Help chat is still loading. Try again in a moment.');
            return;
          }
          window.setTimeout(attemptToggle, 350);
        }, 160);
      };

      attemptToggle();
    },

    async refreshCredits(fallbackDelta = Config.creditsGen) {
      if (!Utils.isLoggedIn()) return;

      try {
        const data = await ApiClient.fetchCredits();
        State.credits = data.credits;
        this.ensureGalleryHistoryFetch();
      } catch (error) {
        console.warn('[Covermatic] Credit refresh failed; preserving last known backend balance', error);
      }
    },

    ensureGalleryHistoryFetch() {
      if (!Utils.isLoggedIn()) return;
      let grid = Utils.$('galleryGrid');
      if (!grid) {
        const host = document.querySelector('#covermatic-app main') || document.querySelector('#covermatic-app') || document.body;
        const section = document.createElement('section');
        section.className = 'space-y-8 w-full min-w-0';
        section.innerHTML = `
          <div class="space-y-4 w-full covermatic-light-copy" data-covermatic-saved-gallery="injected">
            <div class="flex items-center justify-between">
              <h2 id="gallery" class="covermatic-light-title text-xl font-semibold text-gray-900 scroll-mt-32">Recent Covers</h2>
              <div class="covermatic-light-body text-xs text-gray-500">Auto-loads as you scroll</div>
            </div>
            <div id="gallery-scroll-container" class="gallery-scroll scrollbar rounded-xl w-full px-2 sm:px-3 pb-2">
              <div id="grid" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5"></div>
              <div id="fetch-sentinel" class="h-6" aria-hidden="true"></div>
              <div id="fetch-loading" class="hidden text-center text-sm text-gray-600"><span class="inline-block h-4 w-4 border-2 border-gray-300 border-t-gray-900 rounded-full spin" aria-hidden="true"></span> Loading...</div>
              <div id="fetch-end" class="hidden text-center text-sm text-gray-500">No more artwork.</div>
            </div>
          </div>`;
        host.appendChild(section);
        grid = Utils.$('galleryGrid');
      }
      if (!grid || grid.children.length > 0) return;

      try {
        if (!this.gallery) {
          this.gallery = runtime.registerService('gallery', new GalleryController());
          return;
        }
        State.page = 0;
        State.hasMore = true;
        State.isFetching = false;
        State.urlSet = new Set();
        this.gallery.fetchNextPage?.();
      } catch (error) {
        console.warn('[Covermatic] Forced gallery history fetch skipped', error);
      }
    },

    syncUI() {
      const noCreds = State.credits === 0;
      const isPhotomatic = Config.toolSlug === 'photomatic';
      const isFreeCoverArt = State.entryChoice === 'free-cover-art';
      const uploadsInProgress = Number(State.activeUploads || 0) > 0;
      [this.genUploader, this.editUploader].forEach((uploader) => {
        if (!uploader?.zone) return;
        uploader.zone.classList.toggle('opacity-50', (!isPhotomatic && noCreds) || uploader.isUploading);
        const copy = uploader.zone.querySelector('p');
        if (copy) {
          if (uploader.isUploading) {
            copy.textContent = 'Uploading...';
          } else if (!isPhotomatic && noCreds) {
            copy.textContent = 'Credits required';
          } else if (isPhotomatic && uploader.zone.id === 'upload-zone') {
            copy.innerHTML = '<strong>Upload Photo</strong>';
          } else {
            copy.innerHTML = '<span class="font-medium">Click to upload</span> or drag & drop';
          }
        }
      });

      const btn = Utils.$('generateButton');
      if (btn && Utils.isLoggedIn()) {
        if (!this.defaultGenerateButtonHtml) this.defaultGenerateButtonHtml = btn.innerHTML;
        if (isFreeCoverArt) {
          btn.textContent = 'Generate Free Cover';
          btn.disabled = uploadsInProgress;
          btn.onclick = null;
        } else if (!isPhotomatic && noCreds) {
          btn.textContent = 'Buy Credits';
          btn.disabled = false;
          btn.onclick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.openBuyModal();
          };
        } else {
          btn.innerHTML = this.defaultGenerateButtonHtml;
          btn.disabled = uploadsInProgress;
          btn.onclick = null;
        }
      }
    },
  };

  const PublicApp = Object.freeze({
    init: () => App.init(),
    openBuyModal: () => App.openBuyModal(),
    openPromoGraphicsModal: () => App.openPromoGraphicsModal(),
    refreshCredits: (fallbackDelta) => App.refreshCredits(fallbackDelta),
    openHelpChat: () => App.openHelpChat(),
    openEditPanel: () => App.openEditPanel(),
    closeEditPanel: () => App.closeEditPanel(),
    openEditReferencePreview: () => App.openEditReferencePreview(),
    closeEditReferencePreview: () => App.closeEditReferencePreview(),
    updateEditReferencePreview: (url) => App.updateEditReferencePreview(url),
    modals: Object.freeze({
      openImage: (url) => App.modals?.openImage(url),
      openBuyModal: () => App.modals?.openBuyModal(),
      openPromoGraphicsModal: () => App.modals?.openPromoGraphicsModal(),
      selectTemplate: (selection) => App.modals?.selectTemplate(selection),
      selectSignatureStyleAsset: (assetIndex) => App.modals?.selectSignatureStyleAsset(assetIndex),
    }),
  });

  runtime.App = App;
  runtime.PublicApp = PublicApp;
  window.CovermaticApp = PublicApp;
  window.AlphaApp = PublicApp;
  document.addEventListener('DOMContentLoaded', () => {
    App.init().catch((error) => {
      console.error('[Covermatic] App init failed', error);
    });
  });
})();


